import { useEffect, useMemo, useState } from 'react'
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'
import { useAuth } from '../context/useAuth'
import { useWorkspace } from '../context/useWorkspace'
import type { FormResponse } from '../types/forms'

export type UseFormResponsesOptions = {
  workspaceId: string
  formId?: string
  search?: string
  limit?: number
}

export type UseFormResponsesResult = {
  items: FormResponse[]
  loading: boolean
  error: Error | null
  hasMore: boolean
  loadMore: () => void
}

const DEFAULT_PAGE_SIZE = 50
const EMPTY_RESPONSES: FormResponse[] = []

export const useFormResponses = ({
  workspaceId,
  formId,
  search = '',
  limit: initialLimit = DEFAULT_PAGE_SIZE,
}: UseFormResponsesOptions): UseFormResponsesResult => {
  const { user } = useAuth()
  const { canPerformByRole } = useWorkspace()
  const [pageSize, setPageSize] = useState(initialLimit)

  const canReview = canPerformByRole ? canPerformByRole('forms.review_responses') : false
  const userId = user?.uid
  const isEnabled = Boolean(workspaceId && formId && userId)
  const key = isEnabled ? `${workspaceId}:${formId}:${userId}:${canReview}:${pageSize}` : ''

  const [subscribedState, setSubscribedState] = useState<{
    key: string
    items: FormResponse[]
    hasMore: boolean
    error: Error | null
  } | null>(null)

  useEffect(() => {
    if (!workspaceId || !formId || !userId) {
      return
    }

    const currentKey = `${workspaceId}:${formId}:${userId}:${canReview}:${pageSize}`
    const constraints: QueryConstraint[] = [
      where('workspaceId', '==', workspaceId),
      where('formId', '==', formId),
    ]

    if (!canReview) {
      constraints.push(where('submittedBy', '==', userId))
    }

    constraints.push(orderBy('submittedAt', 'desc'))
    constraints.push(limit(pageSize + 1))

    const q = query(collection(firestoreDb, 'form_responses'), ...constraints)

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...(docSnap.data() as Omit<FormResponse, 'id'>),
        }))
        if (docs.length > pageSize) {
          setSubscribedState({
            key: currentKey,
            items: docs.slice(0, pageSize),
            hasMore: true,
            error: null,
          })
        } else {
          setSubscribedState({
            key: currentKey,
            items: docs,
            hasMore: false,
            error: null,
          })
        }
      },
      (err) => {
        setSubscribedState({
          key: currentKey,
          items: EMPTY_RESPONSES,
          hasMore: false,
          error: err instanceof Error ? err : new Error(String(err)),
        })
      },
    )

    return () => unsubscribe()
  }, [workspaceId, formId, userId, canReview, pageSize])

  const isSubscribed = Boolean(isEnabled && subscribedState && subscribedState.key === key)
  const items = isSubscribed && subscribedState ? subscribedState.items : EMPTY_RESPONSES
  const hasMore = isSubscribed && subscribedState ? subscribedState.hasMore : false
  const error = isSubscribed && subscribedState ? subscribedState.error : null
  const loading = Boolean(isEnabled && !isSubscribed)

  const filteredItems = useMemo(() => {
    const trimmed = search.trim().toLowerCase()
    if (!trimmed) return items
    return items.filter((item) => {
      if (item.submittedByName && item.submittedByName.toLowerCase().includes(trimmed)) return true
      if (item.submittedBy.toLowerCase().includes(trimmed)) return true
      if (item.status.toLowerCase().includes(trimmed)) return true
      if (item.data && typeof item.data === 'object') {
        const values = Object.values(item.data)
        return values.some((val) => val != null && String(val).toLowerCase().includes(trimmed))
      }
      return false
    })
  }, [items, search])

  const loadMore = () => {
    if (hasMore && !loading) {
      setPageSize((prev) => prev + DEFAULT_PAGE_SIZE)
    }
  }

  return {
    items: filteredItems,
    loading,
    error,
    hasMore,
    loadMore,
  }
}
